import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shared/ui/button/Button';
import { Card, CardContent, CardHeader } from '@shared/ui/card/Card';
import { Select } from '@shared/ui/select/Select';
import { useLocalStorage } from '@shared/lib/useLocalStorage';
import { defaultPreferences, preferencesSchema } from '../schemas/preferencesSchema';
import type { Preferences } from '../schemas/preferencesSchema';

const currencyOptions = [
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'Dollar américain (USD)' },
  { value: 'GBP', label: 'Livre sterling (GBP)' },
] as const;

const categoryOptions = [
  'Alimentation',
  'Transport',
  'Hébergement',
  'Bureautique',
  'Restauration',
  'Autre',
].map((value) => ({ value, label: value }));

/** Préférences locales de l'espace de travail, persistées dans le navigateur. */
export function SettingsPage() {
  const [savedPreferences, setSavedPreferences] = useLocalStorage<Preferences>(
    'receipt-ai:preferences',
    defaultPreferences,
  );
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Preferences>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: savedPreferences,
  });

  useEffect(() => {
    reset(savedPreferences);
  }, [reset, savedPreferences]);

  const onSubmit = (preferences: Preferences) => {
    setSavedPreferences(preferences);
    reset(preferences);
    setSaved(true);
  };

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Paramètres</h1>
        <p className="mt-1 text-sm text-slate-500">
          Personnalisez les préférences de votre espace. Elles sont enregistrées sur cet appareil.
        </p>
      </div>

      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card>
          <CardHeader
            title="Préférences des tickets"
            subtitle="Utilisées comme valeurs de référence lors de la saisie de vos dépenses."
          />
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select label="Devise principale" options={currencyOptions} {...register('currency')} />
            <Select
              label="Catégorie par défaut"
              options={categoryOptions}
              {...register('defaultCategory')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Notifications"
            subtitle="Choisissez les rappels affichés dans votre espace."
          />
          <CardContent className="flex flex-col gap-4">
            <Toggle
              label="Tickets à revoir"
              description="Recevoir un rappel lorsqu'un ticket nécessite une vérification."
              inputProps={register('reviewNotifications')}
            />
            <Toggle
              label="Résumé hebdomadaire"
              description="Recevoir un rappel pour consulter vos dépenses de la semaine."
              inputProps={register('weeklySummary')}
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!isDirty}>
            Enregistrer les paramètres
          </Button>
          {saved ? (
            <p role="status" className="text-sm text-emerald-700">
              Paramètres enregistrés.
            </p>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function Toggle({
  label,
  description,
  inputProps,
}: {
  label: string;
  description: string;
  inputProps: UseFormRegisterReturn;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-md p-1">
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-slate-800">{label}</span>
        <span className="text-sm text-slate-500">{description}</span>
      </span>
      <input
        type="checkbox"
        className="mt-0.5 size-4 accent-emerald-600"
        aria-label={label}
        {...inputProps}
      />
    </label>
  );
}
